import {
  AUTH_COGNITO_IDENTITY_POOL_ID,
  AUTH_REGION,
  AUTH_USER_POOL_ID,
} from './constants';

/**
 * MapLibre transformRequest signature for ALS V2: given a URL it returns the
 * (possibly SigV4-signed) URL. The helper signs into the query string and never
 * returns headers, so the return is just `{ url }`.
 */
export type MapTransformRequest = (
  url: string,
  resourceType?: string
) => { url: string };

/**
 * Builds a transformRequest authenticated as the current Cognito user.
 *
 * The id token is supplied as a PROVIDER (not a captured string) so the auth
 * helper's internal credential refresh fetches a current token from Amplify on
 * every refresh — keeping multi-hour sessions signing correctly. The helper is
 * built once; its refresh loop plus the transformRequest's getCredentials
 * closure keep the single function valid for the life of the map.
 *
 * The auth helper (and its `@aws-sdk/credential-providers` dependency) and
 * `aws-amplify/auth` are imported dynamically here so the AWS SDK slice is
 * split into its own chunk and only downloads when a map actually mounts —
 * not whenever a module that imports BambiMapView is evaluated.
 */
export async function createMapTransformRequest(): Promise<MapTransformRequest> {
  const [{ withIdentityPoolId }, { fetchAuthSession }] = await Promise.all([
    import('@aws/amazon-location-utilities-auth-helper'),
    import('aws-amplify/auth'),
  ]);

  const loginKey = `cognito-idp.${AUTH_REGION}.amazonaws.com/${AUTH_USER_POOL_ID}`;

  const authHelper = await withIdentityPoolId(AUTH_COGNITO_IDENTITY_POOL_ID, {
    logins: {
      [loginKey]: async () => {
        const { tokens } = await fetchAuthSession();
        return tokens?.idToken?.toString() ?? '';
      },
    },
  });

  return authHelper.getMapAuthenticationOptions().transformRequest;
}
